import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Select from 'react-select'
import { Controller, FieldValues } from 'react-hook-form'

import { t } from '@/i18n'
import { useApp } from '@/hooks'
import InfiniteLoader from './InfiniteLoader'
import { FormError } from './Form'
import { OptionSelectorProps } from './Form/types'

const DropDownPicker = <T extends FieldValues>({
  className,
  label,
  name,
  required,
  control,
  options,
  emptyValue,
  isSearchable,
  isAsync,
  optionsLoading,
  isMenuOpen,
  maxHeight = '300px',
  fetchMoreStatus,
  onFetchMore,
  onFieldChange,
  onSearch,
  isEditing,
  ...rest
}: OptionSelectorProps<HTMLSelectElement, T>) => {
  const [focused, setFocused] = useState<boolean>(false)
  const { isRTL } = useApp()
  const [search, setSearch] = useState<string | null>(null)
  const timer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    if (!isAsync) return
    const debounce = search ? 300 : 0
    if (search !== null) {
      timer.current = setTimeout(() => {
        onSearch?.(search)
      }, debounce)
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [isAsync, onSearch, search])

  if (!name) return null

  return (
    <label className={className}>
      {!!label && (
        <span className={classNames('name', { required: !!required })}>
          {label} {!!required && <sup>*</sup>}
        </span>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          if (!isEditing && [undefined, null, ''].includes(value)) {
            if (emptyValue) {
              return <div className="empty-value">{emptyValue}</div>
            }
          }

          const selectedValue = options?.find(({ value: v }) => v === value)

          if (!isEditing && !selectedValue && emptyValue) {
            return <div className="empty-value">{emptyValue}</div>
          }

          return (
            <>
              <Select
                className={classNames('re-select', { focused })}
                options={options}
                placeholder={
                  optionsLoading
                    ? options?.length
                      ? ''
                      : t('loading')
                    : rest.placeholder
                }
                value={selectedValue}
                isDisabled={rest.disabled}
                isClearable={false}
                isMulti={false}
                isRtl={isRTL}
                menuPosition="fixed"
                closeMenuOnScroll
                menuShouldScrollIntoView={false}
                menuIsOpen={isMenuOpen}
                menuPlacement={rest.menuPlacement || 'auto'}
                isSearchable={isSearchable}
                onInputChange={(s: string) => setSearch(s)}
                onMenuOpen={() => setFocused(true)}
                onMenuClose={() => setFocused(false)}
                components={{
                  MenuList: ({ ...props }) => (
                    <InfiniteLoader
                      maxHeight={maxHeight}
                      fetchMoreStatus={
                        !!search && !isAsync ? 'end' : fetchMoreStatus
                      }
                      onFetchMore={() =>
                        !search || isAsync ? onFetchMore?.() : undefined
                      }
                    >
                      {props.children}
                    </InfiniteLoader>
                  ),
                }}
                onChange={v => {
                  onChange(v?.value)
                  onFieldChange?.(v?.value)
                }}
              />
              {isEditing && <FormError error={error?.message} />}
            </>
          )
        }}
      />
    </label>
  )
}

export default DropDownPicker
